import { useEffect } from 'react';
import type { RefObject } from 'react';

// Trailing debounce after the last observed size change — the panes animate
// width/flex-basis for 0.22s, and re-firing per animation frame would make
// Fortune-Sheet re-layout its canvases ~60× per transition.
const SETTLE_MS = 120;

/**
 * Fortune-Sheet sizes its canvases from the container ONLY on window `resize`
 * (it installs no ResizeObserver of its own) — so a layout-driven width change,
 * like the history/comments panes animating open or closed, leaves the grid at
 * its stale size: a dead gap where the pane was, until the user happens to
 * resize the window. Observe the wrapper and dispatch a window resize once its
 * size settles so the grid reclaims the space immediately.
 *
 * `enabled` must be the wrapper's render gate (same contract as
 * useSheetTabWheelScroll): the wrapper div doesn't exist while the sheet is
 * loading, and a ref dep alone never re-fires this effect when it mounts.
 */
export const useWorkbookResizeSync = (wrapperRef: RefObject<HTMLElement>, enabled: boolean): void => {
    useEffect(() => {
        if (!enabled) return undefined;
        const el = wrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        let timer: number | null = null;
        let last = { w: el.clientWidth, h: el.clientHeight };
        const observer = new ResizeObserver(() => {
            const next = { w: el.clientWidth, h: el.clientHeight };
            if (next.w === last.w && next.h === last.h) return; // initial observe callback
            last = next;
            if (timer) window.clearTimeout(timer);
            timer = window.setTimeout(() => window.dispatchEvent(new Event('resize')), SETTLE_MS);
        });
        observer.observe(el);

        return () => {
            observer.disconnect();
            if (timer) window.clearTimeout(timer);
        };
    }, [wrapperRef, enabled]);
};
